import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useGuestPriceEvents } from 'webReservations/hooks/useGuestPriceEvents';
import { infoToast } from '@components/toasts/Toasts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import styles from './GuestPriceUpdates.scss';

const PRICES_UPDATED_TOAST_ID = 'guest-prices-updated';
const PRICES_UPDATED_TOAST_MS = 10_000;
// Spreads refetches so every guest does not hit a cold availability cache together.
export const REFETCH_JITTER_MAX_MS = 10_000;

/**
 * Keeps a guest on a restaurant's booking pages current when its prices change:
 * refetches the availability data and tells the guest. Renders nothing.
 */
export const GuestPriceUpdates = () => {
  const { restaurantDetails } = useRestaurantContext();
  const { refreshAvailabilities } = useAvailabilityContext();
  const queryClient = useQueryClient();

  const refreshPrices = () => {
    void queryClient.invalidateQueries({
      queryKey: ['restaurants', restaurantDetails.id, 'availabilities'],
    });
    return refreshAvailabilities();
  };

  const jitterTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(jitterTimerRef.current), []);

  useGuestPriceEvents(restaurantDetails.id, () => {
    // One pending refetch covers any further events that arrive meanwhile.
    if (!jitterTimerRef.current) {
      jitterTimerRef.current = setTimeout(() => {
        jitterTimerRef.current = undefined;
        void refreshPrices();
      }, Math.random() * REFETCH_JITTER_MAX_MS);
    }
    infoToast({
      autoClose: PRICES_UPDATED_TOAST_MS,
      message: (
        <span>
          Prices just updated.{' '}
          <button
            className={styles.refresh}
            onClick={() => void refreshPrices()}
            type="button"
          >
            Refresh
          </button>
        </span>
      ),
      toastId: PRICES_UPDATED_TOAST_ID,
    });
  });

  return null;
};
